// src/pages/deliveryNote/DeliveryNoteListPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Truck, Plus, Eye, Calendar, Clock, PackageCheck, XCircle, ShoppingBag, User } from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getDeliveryNotes } from '../../services/api/offlineSales/deliveryNotes';
import { getShops } from '../../services/api/shops';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatDate, formatTime } from '../../utils/dateUtils';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './DeliveryNoteListPage.module.css';

// Only shopId/orderId are read server-side (rettzy-backend .../controllers/deliveryNote.controller.js)
// — no status/date-range/search query params exist yet, so only shopId is wired as a filter here.
const STATUS_ICONS = { pending: Clock, delivered: PackageCheck, cancelled: XCircle };

const statusConfig = {
  pending: { color: '#f59e0b', bg: '#fef3c7' },
  delivered: { color: '#10b981', bg: '#d1fae5' },
  cancelled: { color: '#ef4444', bg: '#fee2e2' },
};

const DeliveryNoteListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { error: showError } = useAlert();

  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [shops, setShops] = useState([]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [20, 50, 100],
  });

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ shopId: '' });

  useEffect(() => {
    (async () => {
      try {
        const result = await getShops();
        setShops(Array.isArray(result) ? result : result?.shops || []);
      } catch (err) {
        console.error('Failed to load shops:', err);
      }
    })();
  }, []);

  useEffect(() => {
    fetchDeliveryNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.currentPage, pagination.pageSize, filters]);

  const fetchDeliveryNotes = async () => {
    try {
      setIsLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
      };
      if (filters.shopId) params.shopId = filters.shopId;

      const response = await getDeliveryNotes(params);
      setDeliveryNotes(response.deliveryNotes || []);
      setPagination(prev => ({ ...prev, total: response.total || 0 }));
    } catch (err) {
      console.error('Failed to fetch delivery notes:', err);
      if (err.response?.status === 403) {
        showError(t('errors.permissionDenied'));
      } else {
        showError(t('deliveryNote.failedToLoadDeliveryNotes'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ shopId: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const hasActiveFilters = () => Object.values(filters).some(v => v !== '');

  const filterDefinitions = [
    {
      key: 'shopId',
      label: t('deliveryNote.shop'),
      type: 'select',
      placeholder: t('deliveryNote.allShops'),
      options: shops.map(s => ({ value: s.id.toString(), label: s.name })),
    },
  ];

  const columns = [
    {
      key: 'deliveryNoteNumber',
      header: t('deliveryNote.deliveryNoteNumber'),
      width: '170px',
      render: (row) => <DocNumberCell number={row.deliveryNoteNumber} to={`/offline-sales/delivery-notes/${row.id}`} />,
    },
    {
      key: 'order',
      header: t('deliveryNote.orderNumber'),
      width: '160px',
      render: (row) => row.order?.orderNumber ? (
        <Link className={tb.docLink} to={`/offline-sales/orders/${row.order.id}`} onClick={(e) => e.stopPropagation()}>
          <ShoppingBag size={12} />{row.order.orderNumber}
        </Link>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'receivedByName',
      header: t('deliveryNote.receivedByName'),
      render: (row) => row.receivedByName
        ? <div className={tb.partyName}><User size={14} />{row.receivedByName}</div>
        : <span className={tb.naText}>—</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '130px',
      render: (row) => {
        const cfg = statusConfig[row.status] || statusConfig.pending;
        const StatusIcon = STATUS_ICONS[row.status] || Clock;
        return (
          <div className={tb.statusBadge} style={{ backgroundColor: cfg.bg, borderColor: cfg.color, color: cfg.color }}>
            <StatusIcon size={14} />
            <span>{t(`deliveryNote.status_${row.status}`)}</span>
          </div>
        );
      },
    },
    {
      key: 'deliveredAt',
      header: t('deliveryNote.deliveredAt'),
      width: '160px',
      render: (row) => row.deliveredAt ? (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.deliveredAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.deliveredAt)}</div>
        </div>
      ) : <span className={tb.naText}>—</span>,
    },
    {
      key: 'createdAt',
      header: t('common.created'),
      width: '160px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const tableActions = {
    render: (row) => (
      <div className={tb.actionsWrapper}>
        <Link
          to={`/offline-sales/delivery-notes/${row.id}`}
          className={`${tb.actionButton} ${tb.viewButton}`}
          title={t('common.viewDetails')}
        >
          <Eye size={14} />
        </Link>
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  return (
    <div className={styles.deliveryNoteListPage}>
      <PageHeader
        title={t('deliveryNote.allDeliveryNotes')}
        description={t('deliveryNote.viewAndManageAllDeliveryNotes')}
        icon={Truck}
        actions={
          hasPermission('create_delivery_note') && (
            <Link to="/offline-sales/delivery-notes/create" className={styles.primaryButton}>
              <Plus size={20} />
              {t('deliveryNote.createDeliveryNote')}
            </Link>
          )
        }
      />

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={deliveryNotes}
        columns={columns}
        actions={tableActions}
        onRowClick={(row) => navigate(`/offline-sales/delivery-notes/${row.id}`)}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={
          hasActiveFilters() ? t('deliveryNote.noDeliveryNotesFound') : t('deliveryNote.noDeliveryNotesYet')
        }
        emptyDescription={
          hasActiveFilters()
            ? t('deliveryNote.noDeliveryNotesFoundDescription')
            : t('deliveryNote.noDeliveryNotesYetDescription')
        }
        emptyIcon={<Truck size={64} />}
        emptyAction={
          hasActiveFilters() ? (
            <button onClick={handleClearFilters} className={styles.primaryButton}>
              {t('common.clearFilters')}
            </button>
          ) : hasPermission('create_delivery_note') ? (
            <Link to="/offline-sales/delivery-notes/create" className={styles.primaryButton}>
              <Plus size={18} />
              {t('deliveryNote.createFirstDeliveryNote')}
            </Link>
          ) : null
        }
      />
    </div>
  );
};

export default DeliveryNoteListPage;
